import { DoorType, MountType, SystemType } from "@/app/@types/shared";
import { Partition } from "../domain";
import { MountHardware } from "../domain/entities/hardware";
import { MountHardwareStrategyRegistry } from "./mount-hardwares-strategies.service";
import { useDictStore } from "@/stores/dictStore";
import { DividerCode } from "../constants";
import { isSingleDoor } from "@/services/estimateService";
import { isKompasDlb } from "../predicates/systems.predicates";
import { useNewEstimateStore } from "@/stores/new/estimateStore";

type TopTrack = {
    structureCode: string;
    qty: number;
};

type CreateMountsHardwaresArgs = {
    installedSystemCode: string;
    activeDoorStructureType: string;
    activeMountStructureType: string;
    partition: Partition;
};

export class MountsService {
    static createMountsHardwares({
        installedSystemCode,
        activeDoorStructureType,
        activeMountStructureType,
        partition,
    }: CreateMountsHardwaresArgs): MountHardware[] {
        if (!partition) {
            return [];
        }

        const mountsStrategiesRegiest = new MountHardwareStrategyRegistry();

        const Strategy = mountsStrategiesRegiest.getStrategy(
            installedSystemCode as SystemType,
            activeDoorStructureType as DoorType,
            activeMountStructureType as MountType,
        );

        if (!Strategy) {
            return [];
        }

        const strategy = new Strategy(partition);
        return strategy.create() as MountHardware[];
    }

    static getTotalWidth(partition: Partition): number {
        return Math.round(partition.dimensions.width.getLength().toFt().getValue()) || 0;
    }

    static getTotalHeight(partition: Partition): number {
        return Math.round(partition.dimensions.height.getLength().toFt().getValue()) || 0;
    }

    static getPertimetr(partition: Partition): number {
        const width = this.getTotalWidth(partition);
        const height = this.getTotalHeight(partition);

        return Math.round(width * 2 + height * 2) || 0;
    }

    static getDoorTotalWidth(partition: Partition): number {
        const door = partition.getActiveDoor();

        return Math.round(door.dimensions.width.getLength().toFt().getValue()) || 0;
    }

    static getDoorTotalHeight(partition: Partition): number {
        const door = partition.getActiveDoor();

        return Math.round(door.dimensions.height.getLength().toFt().getValue()) || 0;
    }

    static getTopFrameWidth(partition: Partition): number {
        return Math.round(this.getPartitionWidthWithoutDoor(partition)) || 0;
    }

    static getBottomFrameWidth(partition: Partition): number {
        return Math.round(this.getPartitionWidthWithoutDoor(partition)) || 0;
    }

    static getPartitionWidthWithoutDoor(partition: Partition): number {
        return Math.round(
            this.getTotalWidth(partition) - this.getDoorTotalWidth(partition)
        ) || 0;
    }

    static getTransomHeight(partition: Partition): number {
        const overdoor = partition.getActiveOverdoor();

        if (overdoor.isTransom()) {
            return Math.round(overdoor.dimensions.height.getLength().toFt().getValue()) || 0;
        }

        return 0;
    }

    static getCGFFrameVertical(partition: Partition): number {
        const door = partition.getActiveDoor();
        const doorHeight = this.getDoorTotalHeight(partition);
        const multiplier = door.isDoubleDoor() ? 6 : 4;

        return Math.round(doorHeight * multiplier) || 0;
    }

    static getCGFFrameHorizontal(partition: Partition): number {
        const doorWidth = this.getDoorTotalWidth(partition);
        const sides = 2;

        return Math.round(doorWidth * sides) || 0;
    }

    static getCGFChannelVertical(partition: Partition): number {
        const partitionWidth = this.getTotalHeight(partition);
        const sides = 2;

        return Math.round(partitionWidth * sides) || 0;
    }

    static getCGFChannelHorizontal(partition: Partition): number {
        const door = partition.getActiveDoor();
        const partitionWidth = this.getTotalWidth(partition);
        const doorWidth = this.getDoorTotalWidth(partition);

        const multiplier = door.isSlidingDoor() ? 1 : 2;

        return Math.round((partitionWidth - doorWidth) * multiplier) || 0;
    }

    static getFunctionalMullionLength(partition: Partition): number {
        const doorWidth = this.getDoorTotalWidth(partition);
        const sides = 4;

        return Math.round(doorWidth * sides) || 0;
    }

    static getFramelessPertimetr(partition: Partition): number {
        const perimeter = this.getPertimetr(partition);
        const doorWidth = this.getDoorTotalWidth(partition);

        return Math.round(perimeter + doorWidth) || 0;
    }

    static getStipJointsQty = (partition: Partition): number => {
        const totalWidth = this.getTotalWidth(partition);
        const door = partition.getActiveDoor();

        const doorSections = 3;
        const singleDoorSections = 2;
        const doubleDoorSections = 3;

        const initialStripesQty = totalWidth / doorSections
        const roundedStripesQty = initialStripesQty < 1 ? 0 : Math.ceil(initialStripesQty);
        let stripesQty = roundedStripesQty - 1;

        if (stripesQty <= 0) {
            return 0
        }

        if (partition.structureType === DividerCode) {
            return stripesQty;
        }

        if (isSingleDoor(door.structureType)) {
            stripesQty = stripesQty - singleDoorSections;
            return stripesQty > 0 ? stripesQty : 0;
        }

        stripesQty = stripesQty - doubleDoorSections;
        return stripesQty > 0 ? stripesQty : 0;
    };

    static getStipJointsWithoutDoorQty = (partition: Partition): number => {
        const totalWidth = this.getTotalWidth(partition);
        const doorWidth = this.getDoorTotalWidth(partition);

        const doorSections = 3;

        return Math.ceil(totalWidth - doorWidth) / doorSections;
    };

    static getTopTrack = (partition: Partition): string => {
        const dictStore = useDictStore();
        const width = partition.dimensions.width.getLength().toInches().getValue();
        const topTrack = dictStore.findMullionTrack(width);

        return topTrack?.code || "";
    };

    static getKompasVerticalLines = (partition: Partition): number => {
        const estimateStore = useNewEstimateStore();
        const systemCode = estimateStore.estimate.installedSystemCode;
        const isDoubleKompas = isKompasDlb(systemCode);
        const multiplier = isDoubleKompas ? 2 : 1;

        const width = MountsService.getTotalWidth(partition);

        const step = 3;
        const sideLine = 1;

        const qty = Math.floor(width / step) - sideLine;

        return Math.round(qty * multiplier) || 0;
    };

    static getKompasHorizontalLines = (): number => {
        const estimateStore = useNewEstimateStore();
        const systemCode = estimateStore.estimate.installedSystemCode;
        const isDoubleKompas = isKompasDlb(systemCode);
        return isDoubleKompas ? 2 : 1;
    };

    static getKompasVerticalLinesLength = (
        partition: Partition,
        lines: number,
    ): number => {
        return Math.round(lines * this.getTotalHeight(partition)) || 0;
    };

    static getKompasHorizontallLinesLength = (
        partition: Partition,
        lines: number,
    ): number => {
        return Math.round(lines * this.getTotalWidth(partition)) || 0;
    };

    static getKompasVerticalCopolymerLines = (partition: Partition): number => {
        const width = MountsService.getTotalWidth(partition);

        const step = 3;
        const sideLine = 1;

        const qty = Math.floor(width / step) - sideLine;

        return Math.floor(qty);
    };

    static getKompasVerticalCopolymerLinesLength = (
        partition: Partition,
        lines: number,
    ): number => {
        return Math.round(lines * this.getTotalHeight(partition)) || 0;
    };

    static isForDoubleDoor = (partition: Partition): boolean => {
        const door = partition.getActiveDoor();

        return door.isDoubleDoor();
    };

    static hasOverdoorTransom = (partition: Partition): boolean => {
        const overdoor = partition.getActiveOverdoor();
        if (!overdoor) {
            return false;
        }
        return overdoor.isTransom();
    };
}
